export const CHECKOUT_URL = 'https://trt-en.impultienda.ar/checkout/'

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/51aE2QNDmSRbF3QTWegTip/TRT-EN.png'

export const KARITO_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/68ef61d6-062e-4db3-b929-12ae97861278.jpg'

export const heroStats = [
  { value: '6', label: 'Clinical Volumes' },
  { value: '300+', label: 'Clinical Protocols' },
  { value: '10', label: 'Decision Algorithms' },
  { value: '30+', label: 'Resolved Cases' },
]

export const promises = [
  {
    title: 'Know when TRT should — and should not — be considered.',
    body: 'Stop relying on a single testosterone value. Follow a structured diagnostic pathway that considers the clinical picture, biochemical findings, and potential underlying causes.',
  },
  {
    title: 'Interpret complex hormone profiles with greater clarity.',
    body: 'Understand the role of total testosterone, free testosterone, SHBG, albumin, LH, FSH, prolactin, and estradiol within the complete clinical context.',
  },
  {
    title: 'Approach difficult patient questions with a structured framework.',
    body: 'Have organized clinical pathways for questions involving fertility, prostate health, cardiovascular considerations, and treatment monitoring.',
  },
  {
    title: 'Know what to monitor after treatment begins.',
    body: 'Follow a structured approach to laboratory monitoring, treatment response, dose adjustments, adverse events, and treatment reassessment.',
  },
  {
    title: 'Stop improvising your clinical process.',
    body: 'Instead of jumping between guidelines, papers, and scattered notes, have your TRT workflow organized in one place.',
  },
]

export const withoutSystem = [
  'Multiple guidelines open at once',
  'Hours searching through medical literature',
  'Uncertainty about whether the patient truly meets criteria',
  'Difficult hormone panels requiring repeated research',
  'No clear sequence from diagnosis to treatment',
  'Uncertainty about what to monitor and when',
]

export const withSystem = [
  'A structured diagnostic pathway',
  'Clinical protocols organized by scenario',
  'Decision algorithms for common TRT questions',
  'Visual laboratory interpretation guides',
  'Treatment and monitoring pathways',
  'A practical reference you can consult when you need it',
]

export const familiarSituations = [
  {
    title: 'You weren’t completely sure whether a patient truly met the criteria for TRT.',
    body: 'You knew the guidelines existed — but finding the exact information you needed during a busy consultation was another story.',
  },
  {
    title: 'You spent far too long reviewing articles before making a decision.',
    body: 'One question led to another paper, another guideline, and another search.',
  },
  {
    title: 'The laboratory results didn’t seem to tell a simple story.',
    body: 'Testosterone, SHBG, free testosterone, LH, FSH, prolactin… suddenly a straightforward consultation became a research session.',
  },
  {
    title: 'You weren’t completely confident about the monitoring plan.',
    body: 'What should be checked? When should it be checked? When should treatment be reassessed?',
  },
  {
    title: 'A patient asked a difficult question about fertility, prostate health, or cardiovascular risk.',
    body: 'And you wanted an answer that was structured, evidence-based, and clinically responsible.',
  },
  {
    title: 'You wished there were a practical system instead of hundreds of pages to review.',
    body: 'Because knowing the information isn’t the same as being able to access the right information when you need it.',
  },
]

export const volumes = [
  {
    number: 'I',
    title: 'The Method for Getting the Diagnosis Right',
    intro:
      'Build a structured approach to identifying patients who may be appropriate candidates for TRT — while recognizing situations where further evaluation or another strategy should come first.',
    lead: 'You’ll explore:',
    items: [
      'Clinical assessment',
      'Biochemical confirmation',
      'Primary vs. secondary hypogonadism',
      'Differential considerations',
      'Reversible causes',
      'Diagnostic decision pathways',
    ],
    footer: null,
  },
  {
    number: 'II',
    title: 'Master Advanced Hormone Interpretation',
    intro: 'Turn complex hormone panels into a structured clinical assessment.',
    lead: 'Learn how to approach:',
    items: [
      'Total testosterone',
      'Free testosterone',
      'SHBG',
      'Albumin',
      'LH and FSH',
      'Prolactin',
      'Estradiol',
      'Hormonal patterns and clinical context',
    ],
    footer:
      'The goal: stop looking at isolated numbers and start interpreting the complete picture.',
  },
  {
    number: 'III',
    title: 'Build a Smarter TRT Treatment Strategy',
    intro:
      'Understand the major testosterone formulations and how treatment selection fits into the patient’s clinical profile.',
    lead: 'Explore:',
    items: [
      'Injectable formulations',
      'Long-acting formulations',
      'Transdermal options',
      'Pharmacokinetic principles',
      'Treatment selection',
      'Therapeutic adjustments',
      'Follow-up considerations',
    ],
    footer: null,
  },
  {
    number: 'IV',
    title: 'Protect Fertility & Hormonal Balance',
    intro: 'Navigate one of the most challenging areas of male hormone therapy.',
    lead: 'Covering:',
    items: [
      'Fertility considerations',
      'hCG',
      'SERMs',
      'Clomiphene',
      'Hormonal suppression',
      'Fertility-preservation strategies',
      'Clinical decision pathways',
    ],
    footer: null,
  },
  {
    number: 'V',
    title: 'Navigate the Patients Who Don’t Fit the Textbook',
    intro: 'Build a more structured approach to complex clinical scenarios involving:',
    lead: null,
    items: [
      'Obesity',
      'Type 2 diabetes',
      'Metabolic syndrome',
      'Cardiovascular considerations',
      'Older patients',
      'Special clinical populations',
      'Risk assessment',
    ],
    footer: null,
  },
  {
    number: 'VI',
    title: 'Monitor TRT Like a Pro',
    intro: 'Because starting therapy is only the beginning.',
    lead: 'Learn how to structure ongoing monitoring around:',
    items: [
      'Treatment response',
      'Testosterone levels',
      'Hematocrit',
      'PSA',
      'Estradiol',
      'Adverse events',
      'Dose reassessment',
      'Treatment adjustments',
      'Long-term follow-up',
      'When treatment should be reconsidered',
    ],
    footer: null,
  },
]

export const includedAssets = [
  {
    title: '300+ Clinical Protocols',
    body: 'Structured pathways covering the most important stages and scenarios throughout the TRT process.',
    icon: 'protocols',
  },
  {
    title: 'Clinical Decision Algorithms',
    body: 'Visual decision trees designed to help you move from clinical question → evaluation → decision → next step.',
    icon: 'algorithms',
  },
  {
    title: 'Hormone Interpretation Guides',
    body: 'Practical visual references for navigating testosterone, SHBG, free testosterone, LH, FSH, prolactin, and estradiol.',
    icon: 'hormones',
  },
  {
    title: 'Treatment Selection Tables',
    body: 'Organized comparisons to help you understand the major TRT formulations and treatment considerations.',
    icon: 'tables',
  },
  {
    title: 'Special-Scenario Protocols',
    body: 'Structured approaches for patients with obesity, diabetes, cardiovascular considerations, fertility concerns, and other challenging scenarios.',
    icon: 'special',
  },
  {
    title: '30+ Resolved Clinical Cases',
    body: 'See how complex TRT scenarios can be approached step by step — from the clinical presentation and laboratory data through the decision-making process.',
    icon: 'cases',
  },
]

export const bonuses = [
  {
    tag: 'BONUS #1',
    title: 'TRT Clinical Algorithm Pack™',
    subtitle: '10 Printable Decision Algorithms',
    intro: 'Get a visual shortcut for the clinical situations you encounter most often.',
    lead: 'Includes structured algorithms for:',
    items: [
      'Diagnostic evaluation',
      'TRT candidacy',
      'Contraindications',
      'Treatment adjustment',
      'Hematocrit',
      'PSA',
      'Estradiol',
      'Fertility',
      'Monitoring',
      'Treatment reassessment',
    ],
    value: '$49',
    image: 'https://cdn.magicpatterns.com/uploads/j1tVRr2je49bd7o6ijTFDq/BONUS_1_TRT-EN.png',
  },
  {
    tag: 'BONUS #2',
    title: 'Resolved TRT Clinical Case Library™',
    subtitle: '30+ Clinical Cases Walked Through Step by Step',
    intro:
      'Don’t just read what a guideline recommends. See how the information can be organized when you’re faced with an actual clinical scenario.',
    lead: 'Each case includes:',
    items: [
      'Clinical presentation',
      'Relevant history',
      'Laboratory findings',
      'Clinical interpretation',
      'Differential considerations',
      'Decision-making pathway',
      'Management considerations',
      'Follow-up',
      'Clinical takeaways',
    ],
    value: '$97',
    image: 'https://cdn.magicpatterns.com/uploads/fXdcm7gUC5Qym8WB7rmHvF/BONUS_2_TRT-EN.png',
  },
  {
    tag: 'BONUS #3',
    title: 'TRT Professional Consultation Kit™',
    subtitle: '8 Practical Clinical Tools Ready to Use',
    intro:
      'A collection of templates designed to help you organize the TRT consultation and follow-up process.',
    lead: 'Includes:',
    items: [
      'TRT-specific clinical history template',
      'Initial laboratory request template',
      'Monitoring sheet',
      'Dose tracking sheet',
      'Symptom tracking sheet',
      'Follow-up checklist',
      'Treatment monitoring timeline',
      'Patient education resources',
    ],
    value: '$67',
    image: 'https://cdn.magicpatterns.com/uploads/qwbHXLVfxdqVr6NQEBGyn2/BONUS_3_TRT-EN.png',
  },
  {
    tag: 'BONUS #4',
    title: 'TRT Clinical Calculator™',
    subtitle: 'Make Common Calculations Faster',
    intro:
      'A practical spreadsheet-based tool designed to help organize relevant calculations and clinical tracking.',
    lead: 'Includes tools for:',
    items: [
      'Free testosterone calculation',
      'BMI',
      'Unit conversions',
      'Treatment intervals',
      'Follow-up dates',
      'Laboratory tracking',
      'Longitudinal patient monitoring',
    ],
    value: '$79',
    image: 'https://cdn.magicpatterns.com/uploads/7qr6KWFDdV5E7ZKtpX4uRp/BONUS_4_TRT-EN.png',
  },
  {
    tag: 'BONUS #5',
    title: 'TRT Complications & Problem-Solving Quick Guide™',
    subtitle: 'Know What to Review When the Case Doesn’t Go as Expected',
    intro: 'A rapid-reference guide covering common TRT management scenarios, including:',
    lead: null,
    items: [
      'Elevated hematocrit',
      'PSA changes',
      'Estradiol concerns',
      'Gynecomastia',
      'Fertility concerns',
      'Lack of expected response',
      'Common adverse effects',
      'Treatment reassessment',
    ],
    value: '$57',
    image: 'https://cdn.magicpatterns.com/uploads/1dE4RrB97zRjh6k8BBwmrA/BONUS_5_TRT-EN.png',
    footer:
      'Each scenario is organized around what to evaluate, what questions to ask, and what clinical considerations should guide the next step.',
  },
]

export const valueStack = [
  { label: 'Master System of TRT™ — 6 Clinical Volumes', value: '$197' },
  { label: 'TRT Clinical Algorithm Pack™', value: '$49' },
  { label: 'Resolved TRT Clinical Case Library™', value: '$97' },
  { label: 'TRT Professional Consultation Kit™', value: '$67' },
  { label: 'TRT Clinical Calculator™', value: '$79' },
  { label: 'TRT Complications & Problem-Solving Quick Guide™', value: '$57' },
]

export const accessIncludes = [
  { title: '6 Clinical Volumes', body: 'A complete journey from diagnostic evaluation through long-term monitoring.' },
  { title: '300+ Clinical Protocols', body: 'Structured references organized around common TRT decisions and scenarios.' },
  { title: 'Decision Algorithms', body: 'Visual pathways designed for fast consultation.' },
  { title: '30+ Resolved Cases', body: 'Learn how the clinical reasoning process can be applied to different scenarios.' },
  { title: '5 Practical Bonuses', body: 'Tools designed to make application and follow-up easier.' },
  { title: 'Digital Access', body: 'Use the material from your computer, tablet, or smartphone.' },
  { title: 'Lifetime Access', body: 'A one-time purchase with ongoing access to the material.' },
  { title: 'Future Updates', body: 'Where applicable, updated versions can be delivered through the same digital access channel.' },
]

export const testimonials = [
  {
    name: 'Dr. Andrés Villalba',
    role: 'Family Medicine · Bogotá, Colombia',
    quote:
      'I used to open three guidelines for every TRT consultation. Now I open one algorithm and I know exactly which pathway the patient belongs to.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c4517f48-e078-46ea-a025-2421f083487c.jpg',
  },
  {
    name: 'Dr. Mariana Cortés',
    role: 'Endocrinology · Monterrey, Mexico',
    quote:
      'The hormone interpretation volume changed how I read a panel. SHBG and free testosterone finally sit inside a clinical context instead of floating alone.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/709316ac-7abb-46a0-9ced-3c351c937dc0.jpg',
  },
  {
    name: 'Dr. Rafael Ibarra',
    role: 'Urology · Santiago, Chile',
    quote:
      'The resolved case library is the part I did not expect to use the most. Seeing the reasoning laid out step by step made my own process much cleaner.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9c555104-5c0e-46f4-84a8-e67a5f3889b0.jpg',
  },
  {
    name: 'Dr. Julián Restrepo',
    role: 'Internal Medicine · Lima, Peru',
    quote:
      'The monitoring sheets and follow-up checklist alone saved me hours in my first week. My consultations are far more structured now.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/93ada506-3990-404d-8fb0-40692421f997.jpg',
  },
]

export const forYou = [
  'Organize your TRT decision-making process',
  'Review clinical concepts more efficiently',
  'Have visual algorithms available for consultation',
  'Work through representative clinical cases',
  'Structure your monitoring workflow',
  'Keep practical TRT resources in one place',
]

export const notDesignedTo = [
  'Replace clinical judgment',
  'Replace current guidelines or official prescribing information',
  'Replace specialist consultation when appropriate',
  'Provide individualized medical advice for a specific patient',
]

export const faqs = [
  {
    q: 'How do I receive the Master System of TRT™?',
    a: 'Immediately after your purchase, you’ll receive digital access instructions. The material can be accessed from a computer, tablet, or smartphone.',
  },
  { q: 'Is this a physical product?', a: 'No. The Master System of TRT™ is a 100% digital educational resource.' },
  {
    q: 'Is this a course?',
    a: 'No. It’s a clinical reference system consisting of six digital volumes, protocols, algorithms, cases, and practical tools.',
  },
  {
    q: 'Who is it designed for?',
    a: 'It is designed primarily for physicians and qualified healthcare professionals involved in the evaluation or management of male hormonal health.',
  },
  {
    q: 'Is the content based on scientific evidence?',
    a: 'The material is structured around recognized clinical guidelines and published scientific evidence. Because medical evidence and recommendations can change, users should always verify current guidelines, official prescribing information, and applicable local requirements.',
  },
  {
    q: 'Can I use it on my phone or tablet?',
    a: 'Yes. The digital resources are designed for convenient access across common devices.',
  },
  { q: 'Is this a subscription?', a: 'No. It’s a one-time purchase.' },
  {
    q: 'How long do I have access?',
    a: 'The offer includes lifetime access to the purchased digital material, subject to the applicable terms.',
  },
]

export const purchaseNotifications = [
  { name: 'Dr. Camila Restrepo', country: 'Medellín, Colombia' },
  { name: 'Dr. Javier Domínguez', country: 'Madrid, Spain' },
  { name: 'Dr. Valentina Ríos', country: 'Buenos Aires, Argentina' },
  { name: 'Dr. Sebastián Molina', country: 'Guadalajara, Mexico' },
  { name: 'Dr. Lucía Fernández', country: 'Montevideo, Uruguay' },
  { name: 'Dr. Mateo Guzmán', country: 'Quito, Ecuador' },
  { name: 'Dr. Daniela Paredes', country: 'Lima, Peru' },
  { name: 'Dr. Emilio Navarro', country: 'Santiago, Chile' },
  { name: 'Dr. Gabriela Suárez', country: 'San José, Costa Rica' },
  { name: 'Dr. Tomás Alcántara', country: 'Asunción, Paraguay' },
  { name: 'Dr. Rocío Delgado', country: 'Barcelona, Spain' },
  { name: 'Dr. Ignacio Bermúdez', country: 'Panama City, Panama' },
]
